/**
 * FAQ content for PH Therapy (pandhtherapy.co.uk)
 * Used for SEO, rich snippets, and user experience.
 */

export interface FaqItem {
  question: string;
  answer: string;
}

export interface FaqSection {
  heading: string;
  items: FaqItem[];
}

export const faqSections: FaqSection[] = [
  {
    heading: 'What We Do',
    items: [
      {
        question: 'What is exercise physiology?',
        answer: 'Exercise physiology studies how the body responds and adapts to physical activity. Clinical Exercise Physiologists use evidence-based exercise prescription to treat injury, manage chronic conditions, and improve performance. At PH Therapy, our AHCS-registered clinicians bridge physiotherapy and strength & conditioning, delivering tailored programmes for partners, CEOs, and professionals across London, Wimbledon, Fulham, Kensington, Chiswick, Surrey, and Sussex.',
      },
      {
        question: "What's the difference between an Exercise Physiologist and a Physiotherapist?",
        answer: 'Physiotherapists focus on diagnosing and treating injuries, pain, and movement disorders, often using manual therapy. Clinical Exercise Physiologists specialise in exercise-based rehabilitation and conditioning for chronic conditions (MSK, cardiac, cancer), injury recovery, and long-term health. At PH Therapy, we blend both approaches, with AHCS-registered clinicians delivering evidence-based exercise programmes that complement physiotherapy and support performance goals.',
      },
      {
        question: 'Who do you typically work with?',
        answer: 'We mainly work with high-performing individuals: partners, CEOs, and corporate leaders. Our clients often have musculoskeletal (MSK) conditions, are recovering from cancer, managing chronic disease, or seeking bespoke rehabilitation and performance. We serve clients across London, Wimbledon, Fulham, Kensington, Chiswick, Surrey, and Sussex.',
      },
    ],
  },
  {
    heading: 'Services & Conditions',
    items: [
      {
        question: 'Do you offer cancer rehabilitation?',
        answer: 'Yes. We specialise in oncology rehabilitation during and after cancer treatment. Our clinicians support fatigue management, rebuilding strength, and restoring energy. Exercise is safe and beneficial at many stages when prescribed appropriately; we tailor programmes to each person.',
      },
      {
        question: 'Can you help with musculoskeletal (MSK) problems?',
        answer: 'Yes. We treat MSK conditions, chronic pain, and injury with evidence-based clinical exercise. Our programmes support recovery, reduce pain, and improve function—whether returning to sport, managing chronic conditions, or aiming for higher performance.',
      },
      {
        question: 'What is lifestyle management at PH Therapy?',
        answer: 'Lifestyle management programmes use personalised exercise and behaviour change to address metabolic health, stress, sleep, and chronic disease. They are tailored for busy executives and professionals who want sustainable long-term improvements.',
      },
      {
        question: 'Do you offer corporate wellness or speaking?',
        answer: 'Yes. We deliver expert-led talks, workshops, and health programmes for organisations across London, Wimbledon, Fulham, Kensington, Chiswick, Surrey, and Sussex, including executive wellness and team resilience.',
      },
    ],
  },
  {
    heading: 'Location & Booking',
    items: [
      {
        question: 'Where are you based?',
        answer: 'We are based in Fulham, West London, and serve clients across London, Wimbledon, Fulham, Kensington, Chiswick, Surrey, and Sussex. We offer in-person consultations and programmes at our clinic; home or corporate visits can be discussed where appropriate.',
      },
      {
        question: 'How do I book a consultation?',
        answer: "Use the contact form on our website or call us. We'll arrange an initial consultation to discuss your goals, health history, and how we can help. Consultations are confidential and tailored to your needs.",
      },
      {
        question: 'Do I need a referral from my doctor?',
        answer: "No. You can self-refer. For significant medical history or specialist care, we may ask for details or a brief summary from your healthcare provider to ensure the safest and most effective programme.",
      },
    ],
  },
  {
    heading: 'Credentials & Approach',
    items: [
      {
        question: 'What does AHCS registration mean?',
        answer: 'AHCS (Allied Health and Care Sciences) registration means our Clinical Exercise Physiologists meet professional standards and are accountable to a regulatory body, so you can be assured of qualified, accredited care.',
      },
      {
        question: 'Is exercise safe during and after cancer treatment?',
        answer: 'Yes, when prescribed appropriately. Research shows exercise can help manage fatigue, improve strength, and support quality of life during and after treatment. Our clinicians have experience in oncology rehabilitation and tailor programmes to your stage of treatment and fitness.',
      },
    ],
  },
  {
    heading: 'Practical',
    items: [
      {
        question: 'What should I expect at my first session?',
        answer: 'Your first session typically includes a detailed discussion of your health and goals, and an assessment of movement, strength, and fitness where useful. From this, we design a personalised programme and agree on next steps.',
      },
      {
        question: 'How long do programmes usually last?',
        answer: 'Programme length varies by goal and condition. Some clients need short rehabilitation; others choose ongoing support for long-term health and performance. We discuss this at the outset and review progress regularly.',
      },
    ],
  },
];

/** All FAQ items in a flat array (for JSON-LD and homepage teaser) */
export const faqItems: FaqItem[] = faqSections.flatMap((s) => s.items);
